// Conservative SQL linter — precision over coverage. Flags ONLY references that
// are certainly wrong against the cached schema, so a squiggle is always
// trustworthy (a linter you learn to ignore is worse than none):
//   1. an unknown table in a top-level FROM/JOIN, and
//   2. an unknown column on a qualified `alias.col` / `table.col` whose table is
//      known (with a non-empty column list).
// NEVER flags bare unqualified columns (could be a CTE/derived/function output),
// never judges a table in a database absent from the cache, and skips anything
// it can't resolve. Pure (offsets relative to the statement text); the CM
// wrapper adds the statement's document offset and gates on a warm cache.
import { tokenize } from './sqlformat.js';
import { hasDb, tableEntry } from './schemaindex.js';

const stripId = (v) => String(v).replace(/`/g, '');

// definedNames collects CTE / derived names (`x AS (`), which are valid FROM
// targets but absent from the schema. Over-collection only SUPPRESSES warnings,
// so a loose regex is safe here.
function definedNames(text) {
  const s = new Set();
  const re = /([`\w]+)\s+as\s*\(/gi;
  let m;
  while ((m = re.exec(text))) s.add(stripId(m[1]).toLowerCase());
  return s;
}

// lintStatement → [{from, to, severity, message}] with offsets INTO `text`.
export function lintStatement(text, index, defaultDb) {
  if (!text || !text.trim() || !index) return [];
  const out = [];
  const defined = definedNames(text);
  const toks = tokenize(text); // single pass feeds both checks

  // ── 1. Unknown FROM/JOIN tables (top level only) + the alias→table map ────
  // The same walk that finds tables captures their aliases, so the column check
  // below needs no second tokenizer pass.
  const tableByQual = new Map(); // qualifier(lower) → {db, table}
  let depth = 0;
  for (let i = 0; i < toks.length; i++) {
    const t = toks[i];
    if (t.t === 'punct' && t.v === '(') { depth++; continue; }
    if (t.t === 'punct' && t.v === ')') { depth = Math.max(0, depth - 1); continue; }
    if (depth !== 0 || t.t !== 'kw') continue;
    const kw = t.v.toUpperCase();
    const isFrom = kw === 'FROM';
    if (!isFrom && kw !== 'JOIN') continue;
    let j = i + 1;
    while (j < toks.length) {
      const nt = toks[j];
      if (!nt || nt.t !== 'ident') break; // `(`subquery / end of clause → stop
      let tableTok = nt, dbName = null;
      if (toks[j + 1]?.t === 'punct' && toks[j + 1].v === '.' && toks[j + 2]?.t === 'ident') {
        dbName = stripId(nt.v); tableTok = toks[j + 2]; j += 2;
      }
      const tableName = stripId(tableTok.v);
      const ref = { db: dbName, table: tableName };
      if (tableName && !defined.has(tableName.toLowerCase()) && (dbName || defaultDb) &&
          hasDb(index, dbName || defaultDb) && !tableEntry(index, dbName || defaultDb, tableName)) {
        out.push({ from: tableTok.start, to: tableTok.end, severity: 'warning',
          message: `Unknown table “${tableName}”${dbName ? ' in ' + dbName : ''}` });
      }
      if (tableName) tableByQual.set(tableName.toLowerCase(), ref);
      j++;
      if (toks[j]?.t === 'kw' && toks[j].v.toUpperCase() === 'AS') j++; // optional alias
      if (toks[j]?.t === 'ident') { tableByQual.set(stripId(toks[j].v).toLowerCase(), ref); j++; }
      if (isFrom && toks[j]?.t === 'punct' && toks[j].v === ',') { j++; continue; } // FROM list
      break;
    }
    i = j - 1;
  }

  // ── 2. Unknown qualified columns (alias.col / table.col / db.table.col) ────
  const seen = new Set();
  for (let i = 0; i + 2 < toks.length; i++) {
    const a = toks[i], dot = toks[i + 1], b = toks[i + 2];
    if (a.t !== 'ident' || dot.t !== 'punct' || dot.v !== '.' || b.t !== 'ident') continue;
    let qualTok = a, colTok = b;
    if (toks[i + 3]?.t === 'punct' && toks[i + 3].v === '.' && toks[i + 4]?.t === 'ident') {
      qualTok = b; colTok = toks[i + 4]; // a.b.c → table=b, col=c
    }
    const tref = tableByQual.get(stripId(qualTok.v).toLowerCase());
    if (!tref) continue; // qualifier isn't a known table/alias in this stmt → skip
    const db = tref.db || defaultDb;
    if (!db || !hasDb(index, db)) continue;
    const entry = tableEntry(index, db, tref.table);
    if (!entry || entry.cols.size === 0) continue; // unknown table / no column info → skip
    const col = stripId(colTok.v);
    if (entry.cols.has(col.toLowerCase())) continue;
    const key = colTok.start + ':' + colTok.end;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ from: colTok.start, to: colTok.end, severity: 'warning',
      message: `Unknown column “${col}” on ${tref.table}` });
  }
  return out;
}
